import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  Portal,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import ChatPreview from '../components/ChatPreview';
import { createStarterGraph, serializeFlowToApi } from '../store/flowBuilderStore';
import { resolveChatWidgetConfig } from '../utils/chatWidgetConfig';
import { API_BASE_URL } from '../config/api';
import { getOrCreateWebUid, generateSessionId } from '../utils/web-uid';

/** Purpose: Perform get preview bot messages for the flows module. Why: Callers need a consistent result from this shared operation. */
const getPreviewBotMessages = (responseData) => {
  if (responseData?.agentTakeover || (responseData?.handoff?.active && responseData?.handoff?.assigned)) return [];
  const source = Array.isArray(responseData?.messagePayloads) && responseData.messagePayloads.length > 0
    ? responseData.messagePayloads
    : Array.isArray(responseData?.messages) && responseData.messages.length > 0
      ? responseData.messages
      : responseData?.response
        ? [responseData.response]
        : responseData?.message
          ? [responseData.message]
          : [];

  return source.filter(Boolean).map((reply) => {
    if (reply && typeof reply === 'object') {
      return {
        role: 'bot',
        text: String(reply.text || reply.caption || ''),
        mediaUrl: String(reply.mediaUrl || reply.url || ''),
        mediaType: String(reply.mediaType || reply.type || ''),
        alt: String(reply.alt || ''),
      };
    }
    return { role: 'bot', text: String(reply) };
  }).filter((reply) => reply.text || reply.mediaUrl);
};

/** Purpose: list and manage saved bot flows. Why: authors need a workspace entry point to create, open, duplicate, and organize journeys. */
export default function Flows() {
  const [flows, setFlows] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newFlowName, setNewFlowName] = useState('');
  const [newFlowDescription, setNewFlowDescription] = useState('');
  const [createMode, setCreateMode] = useState('blank');
  const [importDocument, setImportDocument] = useState(null);
  const [importFileName, setImportFileName] = useState('');
  const [importError, setImportError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewInput, setPreviewInput] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTyping, setPreviewTyping] = useState(false);
  const [previewSessionId, setPreviewSessionId] = useState(() => generateSessionId());
  const [previewMessages, setPreviewMessages] = useState([]);
  const [previewQuickReplies, setPreviewQuickReplies] = useState([]);
  const [previewInputControl, setPreviewInputControl] = useState(null);
  const previewSocketRef = useRef(null);
  const previewConversationIdRef = useRef('');
  const previewRealtimeMessageIdsRef = useRef(new Set());
  const previewRealtimePollRef = useRef(null);
  const previewStateHydratedRef = useRef(false);
  const previewOpenRef = useRef(false);
  const previewLanguageRef = useRef(null);
  const navigate = useNavigate();
  const { selectedBot, selectedEnv, selectedBotDetails } = useAuth();
  const isProd = selectedEnv === 'prod';
  const selectedBotName = selectedBotDetails?.botName || selectedBot || 'Bot Preview';
  const [chatWidgetConfig, setChatWidgetConfig] = useState({});
  const resolvedWidgetConfig = resolveChatWidgetConfig(chatWidgetConfig, selectedBotName);
  const activeFlows = flows.filter((flow) => flow.isActive).length;
  const defaultFlows = flows.filter((flow) => flow.isDefault).length;
  /** Purpose: Resolve total nodes for the flows module. Why: The surrounding operation uses this value to make its decision. */
  const totalNodes = flows.reduce((sum, flow) => sum + (flow.nodes?.length || 0), 0);
  const [headerHost, setHeaderHost] = useState(() => (
    typeof document !== 'undefined' ? document.getElementById('flows-header-actions') : null
  ));

  useEffect(() => {
    /** Purpose: Apply update header host in the flows module. Why: Centralizing this change keeps the relevant state or persisted record consistent. */
    const updateHeaderHost = () => {
      const element = document.getElementById('flows-header-actions');
      if (element && element !== headerHost) {
        setHeaderHost(element);
      }
    };
    updateHeaderHost();
    const frame = window.requestAnimationFrame(updateHeaderHost);
    return () => window.cancelAnimationFrame(frame);
  }, [headerHost]);

  /** Purpose: Perform get preview storage key for the flows module. Why: Callers need a consistent result from this shared operation. */
  const getPreviewStorageKey = () => (
    // Start a clean preview session after the session-state behavior change.
    // Keep v1 in localStorage untouched so previous preview data is recoverable.
    `voicebot:admin-preview:v2:${encodeURIComponent(String(selectedBot || ''))}:${encodeURIComponent(String(selectedEnv || 'dev'))}`
  );

  /** Purpose: Handle disconnect preview realtime in the flows module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const disconnectPreviewRealtime = ({ clearConversation = true } = {}) => {
    if (previewRealtimePollRef.current) {
      window.clearInterval(previewRealtimePollRef.current);
      previewRealtimePollRef.current = null;
    }
    previewSocketRef.current?.removeAllListeners();
    previewSocketRef.current?.disconnect();
    previewSocketRef.current = null;
    if (clearConversation) previewConversationIdRef.current = '';
    previewRealtimeMessageIdsRef.current.clear();
  };

  /** Purpose: Handle connect preview realtime in the flows module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const connectPreviewRealtime = (conversationId, sessionId) => {
    if (!previewOpenRef.current) return;
    const nextConversationId = String(conversationId || '').trim();
    const nextSessionId = String(sessionId || '').trim();
    if (!nextConversationId || !nextSessionId) return;
    if (previewSocketRef.current && previewConversationIdRef.current === nextConversationId) return;

    disconnectPreviewRealtime();
    previewConversationIdRef.current = nextConversationId;
    const socket = io(`${API_BASE_URL || window.location.origin}/widget`, {
      transports: ['websocket', 'polling'],
      auth: { conversationId: nextConversationId, sessionId: nextSessionId },
      reconnection: true,
    });
    previewSocketRef.current = socket;

    /** Purpose: Apply append agent reply in the flows module. Why: Centralizing this change keeps the relevant state or persisted record consistent. */
    const appendAgentReply = (payload = {}) => {
      const incomingConversationId = String(payload.conversationId || payload.message?.conversationId || '').trim();
      if (incomingConversationId && incomingConversationId !== previewConversationIdRef.current) return;
      const incoming = payload.message || payload;
      const text = String(incoming.message || incoming.text || '').trim();
      if (!text && !incoming.audioUrl) return;
      if (incoming.sender && incoming.sender !== 'agent') return;
      const messageId = String(incoming._id || incoming.id || '').trim();
      if (messageId && previewRealtimeMessageIdsRef.current.has(messageId)) return;
      if (messageId) previewRealtimeMessageIdsRef.current.add(messageId);
      setPreviewMessages((current) => [
        ...current,
        { _id: messageId || undefined, role: 'agent', text, mediaUrl: incoming.audioUrl || '', mediaType: incoming.type || 'text' },
      ]);
      setPreviewQuickReplies([]);
      setPreviewInputControl(null);
    };

    // Agent replies are emitted on both events. Listening to both keeps the
    // preview live across older/newer backend emitters; message ids prevent duplicates.
    socket.on('agent_reply', appendAgentReply);
    socket.on('inbox:message-created', appendAgentReply);
    /** Purpose: Apply append journey event in the flows module. Why: Centralizing this change keeps the relevant state or persisted record consistent. */
    const appendJourneyEvent = (payload = {}) => {
      const incomingConversationId = String(payload.conversationId || '').trim();
      if (incomingConversationId && incomingConversationId !== previewConversationIdRef.current) return;
      const responseData = payload.journeyResponse || {};
      const nextMessages = getPreviewBotMessages(responseData);
      if (nextMessages.length) {
        setPreviewMessages((current) => [...current, ...nextMessages]);
      } else if (String(payload.message || '').trim()) {
        setPreviewMessages((current) => [...current, { role: 'bot', text: String(payload.message).trim() }]);
      }
      setPreviewQuickReplies(Array.isArray(responseData.quickReplies) ? responseData.quickReplies : []);
      setPreviewInputControl(responseData.inputControl || null);
    };
    socket.on('conversation:resolved', appendJourneyEvent);
    socket.on('conversation:handoff-timeout', appendJourneyEvent);
    socket.on('connect_error', (error) => {
      console.error('[Preview realtime] connection failed:', error?.message || error);
    });

    // Reconcile agent replies when a proxy/network drops a socket event. This
    // uses the authenticated Inbox API because this preview lives in the admin UI.
    /** Purpose: Load poll agent replies for the flows module. Why: The selected view or service needs current scoped data before continuing. */
    const pollAgentReplies = async () => {
      try {
        const response = await axios.get(`/api/v1/inbox/conversations/${nextConversationId}/messages`, {
          params: { limit: 150, page: 1 },
        });
        (response.data?.messages || [])
          .filter((message) => String(message?.sender || '').toLowerCase() === 'agent')
          .forEach((message) => appendAgentReply({ conversationId: nextConversationId, message }));
      } catch (error) {
        // Socket remains primary; a temporary polling failure is non-fatal.
      }
    };
    previewRealtimePollRef.current = window.setInterval(pollAgentReplies, 2000);
  };

  useEffect(() => () => disconnectPreviewRealtime(), []);

  useEffect(() => {
    previewStateHydratedRef.current = false;
    disconnectPreviewRealtime();

    if (!selectedBot || !selectedEnv) {
      setPreviewMessages([]);
      setPreviewQuickReplies([]);
      setPreviewInputControl(null);
      previewStateHydratedRef.current = true;
      return;
    }

    try {
      const stored = JSON.parse(window.localStorage.getItem(getPreviewStorageKey()) || 'null');
      if (stored?.sessionId) {
        const restoredSessionId = String(stored.sessionId);
        const restoredConversationId = String(stored.conversationId || '').trim();
        setPreviewSessionId(restoredSessionId);
        previewLanguageRef.current = stored.language || null;
        setPreviewMessages(Array.isArray(stored.messages) ? stored.messages.slice(-150) : []);
        setPreviewQuickReplies(Array.isArray(stored.quickReplies) ? stored.quickReplies : []);
        setPreviewInputControl(stored.inputControl && typeof stored.inputControl === 'object' ? stored.inputControl : null);
        previewConversationIdRef.current = restoredConversationId;
      } else {
        setPreviewSessionId(`flows-preview-${Date.now()}`);
        previewLanguageRef.current = null;
        setPreviewMessages([]);
        setPreviewQuickReplies([]);
        setPreviewInputControl(null);
      }
    } catch (error) {
      console.warn('Failed to restore preview session:', error);
      window.localStorage.removeItem(getPreviewStorageKey());
      setPreviewSessionId(`flows-preview-${Date.now()}`);
      previewLanguageRef.current = null;
      setPreviewMessages([]);
      setPreviewQuickReplies([]);
      setPreviewInputControl(null);
    } finally {
      // Let React commit the restored state before enabling persistence; without
      // this guard the first effect pass can overwrite storage with empty defaults.
      window.setTimeout(() => {
        previewStateHydratedRef.current = true;
      }, 0);
    }
  }, [selectedBot, selectedEnv]);

  useEffect(() => {
    if (!previewStateHydratedRef.current || !selectedBot || !selectedEnv || !previewSessionId) return;
    window.localStorage.setItem(getPreviewStorageKey(), JSON.stringify({
      version: 1,
      sessionId: previewSessionId,
      conversationId: previewConversationIdRef.current || '',
      language: previewLanguageRef.current || null,
      messages: previewMessages.slice(-150),
      quickReplies: previewQuickReplies,
      inputControl: previewInputControl,
      updatedAt: Date.now(),
    }));
  }, [selectedBot, selectedEnv, previewSessionId, previewMessages, previewQuickReplies, previewInputControl]);

  useEffect(() => {
    if (selectedBot && selectedEnv) {
      fetchFlows();
    } else {
      setChatWidgetConfig({});
      setLoading(false);
      if (!selectedBot || !selectedEnv) {
        setMessage({ type: 'info', text: 'Please select a bot and environment to view flows.' });
      }
    }
  }, [selectedBot, selectedEnv]);

  /** Purpose: Load resolve environment id for the flows module. Why: The selected view needs current data to render or continue processing. */
  const resolveEnvironmentId = async () => {
    const envResponse = await axios.get(`/api/v1/environments/${selectedBot}/${selectedEnv}`);
    return envResponse.data.environment?.envId || '';
  };

  /** Purpose: Load fetch flows for the flows module. Why: The selected view needs current data to render or continue processing. */
  const fetchFlows = async () => {
    if (!selectedBot || !selectedEnv) {
      setMessage({ type: 'error', text: 'Please select a bot and environment first.' });
      setLoading(false);
      return;
    }

    try {
      // Fetch environment ID first
      const envResponse = await axios.get(`/api/v1/environments/${selectedBot}/${selectedEnv}`);
      const environment = envResponse.data.environment;
      const environmentId = environment?.envId;
      setChatWidgetConfig(environment?.config?.chatWidget || {});

      if (!environmentId) {
        setMessage({ type: 'error', text: 'Environment not found. Please configure it in Settings.' });
        setLoading(false);
        return;
      }

      const response = await axios.get(`/api/v1/flows?botId=${selectedBot}&environmentId=${environmentId}`);
      setFlows(response.data.flows || []);
      setMessage(null);
    } catch (error) {
      console.error('Error fetching flows:', error);
      if (error.response?.status === 401) {
        setMessage({ type: 'error', text: 'Authentication failed. Please login again.' });
      } else if (error.response?.status === 403) {
        setMessage({ type: 'error', text: 'Access denied to this bot.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to load flows' });
      }
    } finally {
      setLoading(false);
    }
  };

  /** Purpose: Handle open create dialog in the flows module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const openCreateDialog = () => {
    setNewFlowName('');
    setNewFlowDescription('');
    setCreateMode('blank');
    setImportDocument(null);
    setImportFileName('');
    setImportError('');
    setCreateDialogOpen(true);
  };

  /** Purpose: Handle select create mode in the flows module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const selectCreateMode = (mode) => {
    setCreateMode(mode);
    setImportError('');
  };

  /** Purpose: Handle handle import file in the flows module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setImportError('Journey JSON must be 2 MB or smaller.');
      return;
    }
    try {
      const parsed = JSON.parse(await file.text());
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('JSON root must be an object.');
      const journey = parsed.journey || parsed.flow || parsed;
      if (!Array.isArray(journey.nodes)) throw new Error('Journey JSON must include a nodes array.');
      setImportDocument(parsed);
      setImportFileName(file.name);
      setNewFlowName(String(journey.name || file.name.replace(/\.json$/i, '') || '').trim());
      setNewFlowDescription(String(journey.description || '').trim());
      setImportError('');
    } catch (error) {
      setImportDocument(null);
      setImportFileName('');
      setImportError(error.message || 'Invalid journey JSON file.');
    }
  };

  /** Purpose: Handle handle create flow in the flows module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleCreateFlow = async () => {
    const trimmedName = newFlowName.trim();
    const trimmedDescription = newFlowDescription.trim();

    if (!selectedBot || !selectedEnv) {
      setMessage({ type: 'error', text: 'Please select a bot and environment first.' });
      return;
    }

    if (!trimmedName) {
      setMessage({ type: 'error', text: 'Flow name is required.' });
      return;
    }

    setCreateLoading(true);
    setMessage(null);

    try {
      const resolvedEnvironmentId = await resolveEnvironmentId();

      if (!resolvedEnvironmentId) {
        throw new Error('Environment not found. Please configure it in Settings.');
      }

      if (createMode === 'import' && !importDocument) {
        throw new Error('Choose a valid journey JSON file first.');
      }
      let response;
      if (createMode === 'import') {
        response = await axios.post('/api/v1/flows/import', {
          botId: selectedBot,
          environmentId: resolvedEnvironmentId,
          document: importDocument,
          name: trimmedName,
          description: trimmedDescription,
        });
      } else {
        const starter = createStarterGraph();
        const payload = serializeFlowToApi({
          ...starter,
          flowName: trimmedName,
          flowDescription: trimmedDescription,
          botId: selectedBot,
          environmentId: resolvedEnvironmentId,
        });
        response = await axios.post('/api/v1/flows', payload);
      }
      const createdFlowId = response.data?.flow?.flowId;

      if (!createdFlowId) {
        throw new Error('Flow was created but no flow id was returned.');
      }

      setCreateDialogOpen(false);
      navigate(`/flows/${createdFlowId}`);
    } catch (createError) {
      setMessage({
        type: 'error',
        text: createError.response?.data?.error || createError.message || 'Failed to create flow',
      });
    } finally {
      setCreateLoading(false);
    }
  };

  /** Purpose: Handle handle export in the flows module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleExport = async (flow) => {
    try {
      const environmentId = await resolveEnvironmentId();
      const response = await axios.get(`/api/v1/flows/${flow.flowId}/export`, {
        params: { botId: selectedBot, environmentId },
      });
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${String(flow.name || 'journey').replace(/[^a-z0-9-_]+/gi, '-')}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: `"${flow.name}" exported successfully.` });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to export journey.' });
    }
  };

  /** Purpose: Handle handle delete in the flows module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleDelete = async (flowId) => {
    if (!window.confirm('Are you sure you want to delete this flow?')) {
      return;
    }

    try {
      const environmentId = await resolveEnvironmentId();
      await axios.delete(`/api/v1/flows/${flowId}`, { params: { botId: selectedBot, environmentId } });
      setMessage({ type: 'success', text: 'Flow deleted successfully' });
      fetchFlows();
    } catch (error) {
      console.error('Error deleting flow:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to delete flow',
      });
    }
  };

  /** Purpose: Handle handle set default in the flows module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleSetDefault = async (flowId) => {
    try {
      const environmentId = await resolveEnvironmentId();
      await axios.put(`/api/v1/flows/${flowId}`, { isDefault: true, botId: selectedBot, environmentId });
      setMessage({ type: 'success', text: 'Default flow updated' });
      fetchFlows();
    } catch (error) {
      console.error('Error setting default flow:', error);
      setMessage({ type: 'error', text: 'Failed to set default flow' });
    }
  };

  /** Purpose: Handle reset preview in the flows module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const resetPreview = () => {
    void goToDefaultJourney();
  };

  /** Purpose: Handle open preview in the flows module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const openPreview = async () => {
    previewOpenRef.current = true;
    setPreviewOpen(true);

    // Always load the latest widget dimensions before opening the preview.
    // Settings may have been changed in another tab while this page stayed open.
    if (selectedBot && selectedEnv) {
      try {
        const response = await axios.get(`/api/v1/environments/${selectedBot}/${selectedEnv}`);
        setChatWidgetConfig(response.data.environment?.config?.chatWidget || {});
      } catch (error) {
        console.error('Failed to refresh chat widget settings:', error);
      }
    }

    // Reopening or refreshing the page must resume the same preview session.
    // A new journey is created only when there is no restorable transcript.
    if (!previewMessages.length || !previewConversationIdRef.current) {
      void goToDefaultJourney();
    } else if (previewConversationIdRef.current) {
      connectPreviewRealtime(previewConversationIdRef.current, previewSessionId);
    }
  };

  const closePreview = () => {
    previewOpenRef.current = false;
    setPreviewOpen(false);
    // Do not keep an inbox socket and message poll running when the preview is
    // hidden. Reopening reconnects to the same restored conversation.
    disconnectPreviewRealtime({ clearConversation: false });
  };

  /** Purpose: Filter sync preview response within the flows module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
  const syncPreviewResponse = (responseData) => {
    if (responseData?.conversationId) {
      previewConversationIdRef.current = responseData.conversationId;
    }
    if (responseData?.language) {
      previewLanguageRef.current = responseData.language;
    }
    setPreviewMessages(getPreviewBotMessages(responseData));
    setPreviewQuickReplies(
      Array.isArray(responseData?.quickReplies)
        ? responseData.quickReplies.filter(Boolean)
        : []
    );
    setPreviewInputControl(
      responseData?.inputControl && typeof responseData.inputControl === 'object'
        ? responseData.inputControl
        : null
    );
  };

  /** Purpose: Handle send preview message in the flows module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const sendPreviewMessage = async (rawText, payload = {}) => {
    const text = (rawText ?? previewInput).trim();
    if (!text || previewLoading) return;

    if (!selectedBot || !selectedEnv) {
      setMessage({ type: 'error', text: 'Please select bot and environment before preview.' });
      return;
    }

    setPreviewMessages((prev) => [...prev, { role: 'user', text }]);
    setPreviewInput('');
    setPreviewQuickReplies([]);
    setPreviewInputControl(null);
    setPreviewLoading(true);
    setPreviewTyping(true);

    try {
      const requestPayload = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
      const webUid = getOrCreateWebUid();
      const response = await axios.post('/api/v1/widget/chat', {
        botId: selectedBot,
        env: selectedEnv,
        message: text,
        uid: webUid,
        userId: webUid,
        sessionId: previewSessionId || generateSessionId(),
        conversationId: previewConversationIdRef.current || undefined,
        payload: requestPayload,
        metadata: {
          uid: webUid,
        },
        clientMessageId: typeof window.crypto?.randomUUID === 'function'
          ? window.crypto.randomUUID()
          : `preview_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      });

      if (response.data?.sessionId && response.data.sessionId !== previewSessionId) {
        setPreviewSessionId(response.data.sessionId);
      }
      if (response.data?.language) {
        previewLanguageRef.current = response.data.language;
      }
      if (response.data?.conversationId) {
        previewConversationIdRef.current = response.data.conversationId;
      }

      connectPreviewRealtime(response.data?.conversationId, response.data?.sessionId || previewSessionId);

      setPreviewMessages((prev) => [
        ...prev,
        ...getPreviewBotMessages(response.data),
      ]);
      setPreviewQuickReplies(
        Array.isArray(response.data?.quickReplies)
          ? response.data.quickReplies.filter(Boolean)
          : []
      );
      setPreviewInputControl(
        response.data?.inputControl && typeof response.data.inputControl === 'object'
          ? response.data.inputControl
          : null
      );
    } catch (error) {
      setPreviewMessages((prev) => [
        ...prev,
        { role: 'bot', text: error.response?.data?.error || 'Preview failed. Please try again.' },
      ]);
      setPreviewQuickReplies([]);
      setPreviewInputControl(null);
    } finally {
      setPreviewLoading(false);
      setPreviewTyping(false);
    }
  };

  /** Purpose: Handle handle preview attachment in the flows module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handlePreviewAttachment = async (file) => {
    if (!file || !selectedBot || previewLoading) return;
    try {
      setPreviewLoading(true);
      const formData = new FormData();
      formData.append('attachment', file);
      formData.append('botId', selectedBot);
      formData.append('env', selectedEnv || 'dev');
      formData.append('sessionId', previewSessionId);
      if (previewConversationIdRef.current) {
        formData.append('conversationId', previewConversationIdRef.current);
      }
      const response = await axios.post('/api/v1/widget/attachments', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data?.conversationId) {
        previewConversationIdRef.current = response.data.conversationId;
      }
      const mediaType = file.type.startsWith('image/')
        ? 'image'
        : file.type.startsWith('video/')
          ? 'video'
          : file.type.startsWith('audio/')
            ? 'audio'
            : 'file';
      setPreviewMessages((prev) => [
        ...prev,
        {
          role: 'user',
          text: file.name,
          mediaUrl: URL.createObjectURL(file),
          mediaType,
          alt: file.name,
        },
      ]);
    } catch (error) {
      console.error('Failed to upload preview attachment:', error);
      setPreviewMessages((prev) => [
        ...prev,
        { role: 'bot', text: error.response?.data?.error || 'Attachment upload failed.' },
      ]);
    } finally {
      setPreviewLoading(false);
    }
  };

  /** Purpose: Resolve go to default journey for the flows module. Why: The surrounding operation uses this value to make its decision. */
  const goToDefaultJourney = async () => {
    if (previewLoading) return;

    if (!selectedBot || !selectedEnv) {
      setMessage({ type: 'error', text: 'Please select bot and environment before preview.' });
      return;
    }

    const activeSessionId = `flows-preview-${generateSessionId()}`;
    const preservedLanguage = previewLanguageRef.current || null;
    const webUid = getOrCreateWebUid();

    disconnectPreviewRealtime();
    previewConversationIdRef.current = '';
    setPreviewSessionId(activeSessionId);
    setPreviewQuickReplies([]);
    setPreviewInputControl(null);
    setPreviewMessages([
      { role: 'bot', text: 'Opening default journey...' },
    ]);
    setPreviewLoading(true);
    setPreviewTyping(true);

    try {
      // Use the same bootstrap endpoint as the embedded widget. It resolves
      // the active default flow and starts at its start node; sending "home"
      // to /chat can be interpreted as ordinary journey input instead.
      const response = await axios.post('/api/v1/widget/bootstrap', {
        botId: selectedBot,
        env: selectedEnv,
        uid: webUid,
        userId: webUid,
        sessionId: activeSessionId,
        metadata: {
          uid: webUid,
          ...(preservedLanguage ? { preferredLanguage: preservedLanguage, language: preservedLanguage } : {}),
        },
      });

      if (response.data?.sessionId) {
        setPreviewSessionId(response.data.sessionId);
      }
      if (response.data?.language) {
        previewLanguageRef.current = response.data.language;
      }
      if (response.data?.conversationId) {
        previewConversationIdRef.current = response.data.conversationId;
      }

      connectPreviewRealtime(response.data?.conversationId, response.data?.sessionId || activeSessionId);

      syncPreviewResponse(response.data || {});
    } catch (error) {
      setPreviewMessages([
        { role: 'bot', text: error.response?.data?.error || 'Failed to open default journey.' },
      ]);
      setPreviewQuickReplies([]);
      setPreviewInputControl(null);
    } finally {
      setPreviewLoading(false);
      setPreviewTyping(false);
    }
  };

  /** Purpose: Perform filtered flows for the flows module. Why: Callers need a consistent result from this shared operation. */
  const filteredFlows = flows.filter((flow) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (flow.name && flow.name.toLowerCase().includes(q)) ||
      (flow.description && flow.description.toLowerCase().includes(q))
    );
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  const headerActionsContent = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        minWidth: 0,
        width: '100%',
        flexWrap: headerHost ? 'nowrap' : 'wrap',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flexWrap: headerHost ? 'nowrap' : 'wrap' }}>
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            gap: 0.75,
            px: 0.5,
            flexShrink: 0,
          }}
        >
          <AccountTreeIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 800,
              fontSize: '0.8125rem',
              letterSpacing: '-0.02em',
              lineHeight: 1,
              /** Purpose: Derive the color style for the flows view. Why: Theme and state dependent styling should be calculated from current UI context. */
              color: (theme) => (theme.palette.mode === 'dark' ? '#f1f5f9' : '#0f172a'),
              userSelect: 'none',
            }}
          >
            Flows
          </Typography>
        </Box>

        {(flows.length > 0 || searchQuery) && (
          <TextField
            size="small"
            placeholder="Search flows..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ p: 0.25 }}>
                    <ClearIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              minWidth: { xs: 140, sm: 180, md: 220 },
              maxWidth: { xs: 200, sm: 260, md: 300 },
              '& .MuiOutlinedInput-root': {
                height: 34,
                borderRadius: 2,
                fontSize: '0.8125rem',
                /** Purpose: Derive the bgcolor style for the flows view. Why: Theme and state dependent styling should be calculated from current UI context. */
                bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.85)' : 'rgba(255,255,255,0.95)'),
              },
            }}
          />
        )}

        <Chip
          label={`${filteredFlows.length} ${filteredFlows.length === 1 ? 'journey' : 'journeys'}`}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 700,
            fontSize: '0.75rem',
            display: { xs: 'none', md: 'inline-flex' },
            height: 32,
            borderRadius: 2,
            px: 0.5,
            /** Purpose: Derive the border color style for the flows view. Why: Theme and state dependent styling should be calculated from current UI context. */
            borderColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(226,232,240,0.9)'),
            /** Purpose: Derive the bgcolor style for the flows view. Why: Theme and state dependent styling should be calculated from current UI context. */
            bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.6)' : 'rgba(255,255,255,0.85)'),
            flexShrink: 0,
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        {!isProd && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<PlayArrowIcon sx={{ fontSize: 16 }} />}
            onClick={openPreview}
            sx={{
              height: 34,
              borderRadius: 2,
              px: 1.5,
              fontWeight: 650,
              fontSize: '0.8rem',
              textTransform: 'none',
              whiteSpace: 'nowrap',
              /** Purpose: Derive the border color style for the flows view. Why: Theme and state dependent styling should be calculated from current UI context. */
              borderColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(203,213,225,0.9)'),
              /** Purpose: Derive the bgcolor style for the flows view. Why: Theme and state dependent styling should be calculated from current UI context. */
              bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.6)' : 'rgba(255,255,255,0.8)'),
              '&:hover': {
                /** Purpose: Derive the bgcolor style for the flows view. Why: Theme and state dependent styling should be calculated from current UI context. */
                bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(30,41,59,0.9)' : 'rgba(241,245,249,0.95)'),
              },
            }}
          >
            Preview
          </Button>
        )}

        {!isProd && (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
            onClick={() => navigate('/deployments')}
            sx={{
              height: 34,
              borderRadius: 2,
              px: 1.5,
              fontWeight: 650,
              fontSize: '0.8rem',
              textTransform: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            Publish
          </Button>
        )}

        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={openCreateDialog}
          sx={{
            height: 34,
            borderRadius: 2,
            px: 2,
            fontWeight: 700,
            fontSize: '0.8rem',
            textTransform: 'none',
            whiteSpace: 'nowrap',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          }}
        >
          Create Flow
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
      <Portal container={headerHost} disablePortal={!headerHost}>
        {headerActionsContent}
      </Portal>

      {message && (
        <Alert severity={message.type} sx={{ mb: 0.5 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {isProd && (
        <Alert severity="info" sx={{ mb: 0.5 }}>
          PROD flows are read-only. Make changes in DEV and release them through the Release Center.
        </Alert>
      )}

      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
          borderRadius: 2,
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <TableContainer
          sx={{
            maxHeight: 'calc(100vh - 150px)',
            overflowY: 'auto',
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.03)',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(100,116,139,0.35)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(100,116,139,0.55)',
            },
          }}
        >
            <Table stickyHeader size="medium">
              <TableHead>
                <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.06em', py: 1.5, px: 2.5, bgcolor: 'background.paper', zIndex: 2, borderBottom: '1px solid', borderColor: 'divider', color: 'text.secondary' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.06em', py: 1.5, px: 2.5, bgcolor: 'background.paper', zIndex: 2, borderBottom: '1px solid', borderColor: 'divider', color: 'text.secondary' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.06em', py: 1.5, px: 2, bgcolor: 'background.paper', zIndex: 2, borderBottom: '1px solid', borderColor: 'divider', color: 'text.secondary' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.06em', py: 1.5, px: 2, bgcolor: 'background.paper', zIndex: 2, borderBottom: '1px solid', borderColor: 'divider', color: 'text.secondary' }}>Default</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.06em', py: 1.5, px: 2, bgcolor: 'background.paper', zIndex: 2, borderBottom: '1px solid', borderColor: 'divider', color: 'text.secondary' }}>Nodes</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.06em', py: 1.5, px: 2.5, bgcolor: 'background.paper', zIndex: 2, borderBottom: '1px solid', borderColor: 'divider', color: 'text.secondary' }}>Updated</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.06em', py: 1.5, px: 2.5, bgcolor: 'background.paper', zIndex: 2, borderBottom: '1px solid', borderColor: 'divider', color: 'text.secondary' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredFlows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box sx={{ py: 8 }}>
                      <Typography variant="h6" gutterBottom>
                        {searchQuery ? 'No flows match your search' : 'No flows yet'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {searchQuery
                          ? `No flow found matching "${searchQuery}". Clear search or try a different term.`
                          : isProd
                            ? 'No flow has been deployed to PROD yet.'
                            : 'Create your first flow to start building a journey.'}
                      </Typography>
                      {searchQuery ? (
                        <Button variant="outlined" size="small" onClick={() => setSearchQuery('')}>
                          Clear Search
                        </Button>
                      ) : !isProd ? (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
                          Create Flow
                        </Button>
                      ) : null}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredFlows.map((flow) => (
                  <TableRow
                    key={flow._id || flow.flowId}
                    hover
                    sx={{
                      transition: 'background-color 0.15s ease',
                      '&:hover': {
                        /** Purpose: Derive the bgcolor style for the flows view. Why: Theme and state dependent styling should be calculated from current UI context. */
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(15,23,42,0.02)',
                      },
                      '& td': {
                        py: { xs: 1.2, md: 1.35 },
                      },
                    }}
                  >
                    <TableCell sx={{ minWidth: 200, px: 2.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            /** Purpose: Derive the bgcolor style for the flows view. Why: Theme and state dependent styling should be calculated from current UI context. */
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.15)' : '#eff6ff',
                            color: '#2563eb',
                            flexShrink: 0,
                          }}
                        >
                          <AccountTreeIcon sx={{ fontSize: 17 }} />
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.95rem',
                              color: 'text.primary',
                              lineHeight: 1.3,
                            }}
                          >
                            {flow.name}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 240, maxWidth: 450, px: 2.5 }}>
                      <Typography
                        sx={{
                          fontSize: '0.9rem',
                          color: 'text.secondary',
                          lineHeight: 1.45,
                          wordBreak: 'break-word',
                        }}
                      >
                        {flow.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 100, px: 2 }}>
                      <Chip
                        label={flow.isActive ? 'Active' : 'Inactive'}
                        color={flow.isActive ? 'success' : 'default'}
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          height: 26,
                          px: 0.5,
                          borderRadius: '6px',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 120, px: 2 }}>
                      {flow.isDefault ? (
                        <Chip
                          label="Default"
                          color="primary"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            height: 26,
                            px: 0.5,
                            borderRadius: '6px',
                          }}
                        />
                      ) : !isProd ? (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleSetDefault(flow.flowId)}
                          sx={{
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            color: 'primary.main',
                            px: 1,
                            py: 0.25,
                            borderRadius: '6px',
                            '&:hover': {
                              /** Purpose: Derive the bgcolor style for the flows view. Why: Theme and state dependent styling should be calculated from current UI context. */
                              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.12)' : 'rgba(37, 99, 235, 0.08)',
                            },
                          }}
                        >
                          Set Default
                        </Button>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 90, px: 2 }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 30,
                          height: 26,
                          px: 1,
                          borderRadius: '6px',
                          /** Purpose: Derive the bgcolor style for the flows view. Why: Theme and state dependent styling should be calculated from current UI context. */
                          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.05)',
                          color: 'text.primary',
                          fontWeight: 700,
                          fontSize: '0.88rem',
                        }}
                      >
                        {flow.nodes?.length || 0}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 120, px: 2.5 }}>
                      <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', fontWeight: 500 }}>
                        {new Date(flow.updatedAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 150, px: 2.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleExport(flow)}
                          title="Export journey JSON"
                          sx={{
                            p: 0.75,
                            borderRadius: '6px',
                            color: 'text.secondary',
                            '&:hover': {
                              /** Purpose: Derive the bgcolor style for the flows view. Why: Theme and state dependent styling should be calculated from current UI context. */
                              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.06)',
                              color: 'text.primary',
                            },
                          }}
                        >
                          <DownloadIcon sx={{ fontSize: 19 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/flows/${flow.flowId}`)}
                          title={isProd ? 'View' : 'Edit'}
                          sx={{
                            p: 0.75,
                            borderRadius: '6px',
                            color: 'text.secondary',
                            '&:hover': {
                              /** Purpose: Derive the bgcolor style for the flows view. Why: Theme and state dependent styling should be calculated from current UI context. */
                              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.15)' : '#eff6ff',
                              color: '#2563eb',
                            },
                          }}
                        >
                          {isProd ? <VisibilityIcon sx={{ fontSize: 19 }} /> : <EditIcon sx={{ fontSize: 19 }} />}
                        </IconButton>
                        {!isProd && (
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(flow.flowId)}
                            color="error"
                            title="Delete"
                            sx={{
                              p: 0.75,
                              borderRadius: '6px',
                              '&:hover': {
                                /** Purpose: Derive the bgcolor style for the flows view. Why: Theme and state dependent styling should be calculated from current UI context. */
                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.08)',
                              },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 19 }} />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={createDialogOpen}
        onClose={() => !createLoading && setCreateDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{createMode === 'import' ? 'Import journey' : 'Create flow'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <Button
              variant={createMode === 'blank' ? 'contained' : 'outlined'}
              startIcon={<AddIcon />}
              onClick={() => selectCreateMode('blank')}
            >
              Blank journey
            </Button>
            <Button
              variant={createMode === 'import' ? 'contained' : 'outlined'}
              startIcon={<UploadFileIcon />}
              onClick={() => selectCreateMode('import')}
            >
              Import JSON
            </Button>
          </Box>
          {createMode === 'import' && (
            <Box
              sx={{ border: '1px dashed', borderColor: importError ? 'error.main' : 'divider', borderRadius: 2, p: 2 }}
            >
              <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
                Choose JSON file
                <input hidden type="file" accept="application/json,.json" onChange={handleImportFile} />
              </Button>
              <Typography variant="body2" color={importError ? 'error' : 'text.secondary'} sx={{ mt: 1 }}>
                {importError || importFileName || 'Exported VoiceBot journey JSON, maximum 2 MB.'}
              </Typography>
            </Box>
          )}
          <TextField
            label="Flow name"
            placeholder="For example: Lead capture"
            value={newFlowName}
            onChange={(event) => setNewFlowName(event.target.value)}
            autoFocus
            fullWidth
          />
          <TextField
            label="Description"
            placeholder="Short summary of what this flow does"
            value={newFlowDescription}
            onChange={(event) => setNewFlowDescription(event.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={createLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateFlow}
            disabled={createLoading || !newFlowName.trim() || (createMode === 'import' && !importDocument)}
          >
            {createLoading ? 'Creating…' : createMode === 'import' ? 'Import Journey' : 'Create Flow'}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={previewOpen}
        onClose={closePreview}
        hideBackdrop
        disableScrollLock
        ModalProps={{
          disableEnforceFocus: true,
          disableAutoFocus: true,
          disableRestoreFocus: true,
          disableScrollLock: true,
          sx: {
            pointerEvents: 'none',
          },
        }}
        sx={{
          pointerEvents: 'none',
          /** Purpose: Derive the z index style for the flows view. Why: Theme and state dependent styling should be calculated from current UI context. */
          zIndex: (theme) => theme.zIndex.drawer + 2,
          '& .MuiDrawer-paper': {
            pointerEvents: 'auto',
          },
        }}
        PaperProps={{
          sx: {
            pointerEvents: 'auto',
            width: { xs: '100%', sm: `${resolvedWidgetConfig.width}px` },
            maxWidth: { xs: '100%', sm: 'calc(100vw - 32px)' },
            top: { xs: 0, sm: 'auto' },
            right: { xs: 0, sm: 16 },
            bottom: { xs: 0, sm: 16 },
            height: { xs: '100dvh', sm: `${resolvedWidgetConfig.height}px` },
            maxHeight: { xs: '100dvh', sm: 'calc(100dvh - 32px)' },
            minHeight: 0,
            boxSizing: 'border-box',
            border: { xs: 0, sm: '1px solid' },
            borderColor: 'divider',
            borderRadius: { xs: 0, sm: '24px' },
            overflow: 'hidden',
            boxShadow: { sm: '0 24px 70px rgba(15, 23, 42, 0.28)' },
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ChatPreview
              title={resolvedWidgetConfig.title}
              subtitle={resolvedWidgetConfig.subtitle}
              botName={resolvedWidgetConfig.title}
              status={previewTyping ? 'Typing...' : resolvedWidgetConfig.statusText}
              avatarText={resolvedWidgetConfig.avatarText}
              avatarUrl={resolvedWidgetConfig.avatarUrl}
              showHeader={resolvedWidgetConfig.showHeader}
              showAvatar={resolvedWidgetConfig.showAvatar}
              showTitle={resolvedWidgetConfig.showTitle}
              showSubtitle={resolvedWidgetConfig.showSubtitle}
              showStatus={resolvedWidgetConfig.showStatus}
              showReset={resolvedWidgetConfig.showReset}
              showHome={resolvedWidgetConfig.showHome}
              showWatermark={resolvedWidgetConfig.showWatermark}
              resetLabel={resolvedWidgetConfig.resetLabel}
              messages={previewMessages}
              typing={previewTyping}
              quickReplies={previewQuickReplies}
              preserveQuickReplyPayload
              inputValue={previewInput}
              onInputChange={setPreviewInput}
              onSend={(value, payload) => sendPreviewMessage(value, payload)}
              onQuickReply={(reply) => {
                const isObj = reply && typeof reply === 'object';
                const value = isObj ? (reply.value || reply.text || reply.title || reply.label) : reply;
                const payload = isObj && reply.payload ? reply.payload : {};
                sendPreviewMessage(value, payload);
              }}
              onAttachment={handlePreviewAttachment}
              onReset={resetPreview}
              onHome={goToDefaultJourney}
              onClose={closePreview}
              inputType={previewInputControl?.inputType || 'text'}
              dateValidation={previewInputControl?.dateValidation || {}}
              placeholder={previewInputControl?.placeholder || resolvedWidgetConfig.inputPlaceholder}
              footerHint=""
              themeMode={resolvedWidgetConfig.themeMode}
              primaryColor={resolvedWidgetConfig.primaryColor}
              accentColor={resolvedWidgetConfig.brandColorMode === 'gradient' ? resolvedWidgetConfig.secondaryColor : resolvedWidgetConfig.accentColor}
              complementaryColor={resolvedWidgetConfig.complementaryColor}
              fontFamily={resolvedWidgetConfig.fontFamily}
              fontSize={resolvedWidgetConfig.fontSize}
              textSize={resolvedWidgetConfig.textSize}
              textColor={resolvedWidgetConfig.textColor}
            />
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}
